import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';

import { fetchPaymentRequestVoucherDetail } from '#/api/fmsMgmt/paymentRequest';
import { createLinkedVoucherAttachmentsApi, printVoucher } from '#/api/fmsMgmt/voucher';
import { ATTACHMENT_POLICY_STANDARD } from '#/api/platform/attachments';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { DocumentAttachmentsCard } from '#/modules/commonComponents/attachments';
import { Button } from '#/modules/commonComponents/button';
import VoucherMainInfoCard from '#/modules/finMgmt/vouchers/components/VoucherMainInfoCard';
import useFmsPrintPdf from '#/modules/finMgmt/shared/hooks/useFmsPrintPdf';
import { getVoucherApiErrorMessage } from '#/modules/finMgmt/vouchers/helper/apiError';
import PageShell from '#/modules/infra/layout/PageShell';
import { paymentRequestVoucherDetailQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const BACK_TO = {
  personal: ROUTES.personalCenterClaimDetail,
  finance: ROUTES.fmsPaymentRequestDetail,
};

/**
 * 由請款單開啟的關聯憑證（唯讀）。
 * GET `/fms/payment-requests/{id}/vouchers/{voucherId}`；附件 GET 帶 `payment_request_id`。
 * 此入口不賦予憑證寫入權，故不放指令列、附件唯讀；只提供列印（帶 `payment_request_id`）。
 */
const PaymentRequestVoucherDetailPage = ({ mode = 'personal' }) => {
  const { id, voucherId } = useParams();
  const detailQuery = useQuery({
    queryKey: paymentRequestVoucherDetailQueryKey(id, voucherId),
    queryFn: () => fetchPaymentRequestVoucherDetail(id, voucherId),
    enabled: Boolean(id && voucherId),
  });
  const attachmentsApi = useMemo(() => createLinkedVoucherAttachmentsApi(id), [id]);
  const { isPrinting, print } = useFmsPrintPdf(
    () => printVoucher(voucherId, {
      paymentRequestId: id,
      voucherNo: detailQuery.data?.data?.voucherNo,
    }),
    getVoucherApiErrorMessage,
  );

  useEffect(() => {
    if (!detailQuery.isError) {
      return;
    }
    toast.error(getVoucherApiErrorMessage(detailQuery.error, '載入憑證失敗'));
  }, [detailQuery.isError, detailQuery.error]);

  const detail = detailQuery.data?.data;

  return (
    <PageShell variant="detail">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div
            className={cn(
              'sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-2',
              'border-b border-slate-200 bg-canvas px-1 pb-3 pt-1',
            )}
          >
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">引用憑證</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={BACK_TO[mode](id)}>返回請款單</Link>
            </Button>
            {detailQuery.data?.data ? (
              <Button
                type="button"
                variant="outline"
                className="ml-auto"
                disabled={isPrinting}
                onClick={print}
              >
                <Printer className="size-4" aria-hidden />
                {isPrinting ? '列印中…' : '列印'}
              </Button>
            ) : null}
          </div>

          {detailQuery.isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {detailQuery.isError ? (
            <p className="text-sm text-destructive">
              {getVoucherApiErrorMessage(detailQuery.error, '載入憑證失敗')}
            </p>
          ) : null}

          {detail ? (
            <>
              <VoucherMainInfoCard detail={detail} />
              <DocumentAttachmentsCard
                businessId={String(detail.id)}
                ownerKey={`fmsPaymentRequestVoucher:${id}`}
                api={attachmentsApi}
                policy={ATTACHMENT_POLICY_STANDARD}
                readOnly
              />
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

PaymentRequestVoucherDetailPage.propTypes = {
  mode: PropTypes.oneOf(['personal', 'finance']),
};

export default PaymentRequestVoucherDetailPage;
